/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { jsonToHTML } from '@contentstack/utils';
import { ContentStackSDK, ContentStackPreviewSDK } from '../stack.server';
import logger from '@/utils/logger';
import { getLocaleForProxiedHostname, getProxiedHostname } from '@/utils/getInitialAppState';
import { type ParsedUrlQuery } from 'querystring';
import { type NextApiRequest } from 'next';
import { type PageContextType } from '@/next-types/index';
import { isContentStackError } from '../../utils/typeGuards/isContentStackError';
import { type LivePreviewQuery, type Query, type Stack } from 'contentstack';
import { getCMSLocale } from '../../utils/getCMSLocale';
import { type Locale } from '@/types/i18n';

type ContentTypeUID =
  | 'header'
  | 'navigation_menu'
  | 'homepage'
  | 'default_pdp'
  | 'premium_pdp'
  | 'category_pages'
  | 'product_listing_page'
  | 'shelf_page'
  | 'merchandising_shelf_page'
  | 'landing_pages'
  | 'default_brand_page'
  | 'premium_brand_pages'
  | 'footer'
  | 'post'
  | 'terms_conditions'
  | 'blog_menu_navigation'
  | 'navigation_links'
  | 'post_reviewer'
  | 'store_page'
  | 'account_page'
  | 'ymme_aq_helper'
  | 'order_history_page'
  | 'order_details_page'
  | 'experience_injector_v2'
  | 'locations_seo_metadata'
  | 'enhanced_pdp_content'
  | 'rate_review'
  | 'my_warranties_page';

type CustomizeQueryCallback = (params: {
  query: Query;
  sdk: Stack;
  contentTypeUid: string;
  language: string;
  queryParams: ParsedUrlQuery;
}) => Query;

type BaseOptions = {
  contentTypeUid: ContentTypeUID;
  jsonRtePath?: string[];
  referenceFieldPath?: string[];
  referenceFieldOnly?: Record<string, string[]>;
  req: NextApiRequest | PageContextType['req'];
  query: ParsedUrlQuery;
  /** Override the language derived from the request hostname */
  languageOverride?: string | null;
  customizeQuery?: CustomizeQueryCallback;
  limit?: number;
};

type SingleOptions = BaseOptions & {
  multiple?: false;
};

type MultipleOptions = BaseOptions & {
  multiple: true;
};

function isContentEntry<T>(maybeContentEntry: unknown): maybeContentEntry is T {
  return !!maybeContentEntry && typeof maybeContentEntry === 'object';
}

export async function getContentByContentType<ContentTypeSchema>(
  options: SingleOptions,
): Promise<ContentTypeSchema>;

export async function getContentByContentType<ContentTypeSchema>(
  options: MultipleOptions,
): Promise<ContentTypeSchema[]>;

export async function getContentByContentType<ContentTypeSchema>({
  contentTypeUid,
  jsonRtePath,
  referenceFieldPath,
  referenceFieldOnly,
  req,
  query,
  languageOverride,
  multiple = false,
  customizeQuery,
  limit,
}: SingleOptions | MultipleOptions): Promise<ContentTypeSchema | ContentTypeSchema[]> {
  const proxiedHostname = getProxiedHostname(req);
  const language = getCMSLocale(
    (languageOverride as Locale | undefined) || getLocaleForProxiedHostname(proxiedHostname),
  );

  const pagetype = query.pageType;

  const livePreview =
    typeof query.live_preview === 'string' && typeof query.content_type_uid === 'string';
  const timelinePreview =
    typeof query.live_preview === 'string' && typeof query.preview_timestamp === 'string';
  const isPreview = livePreview || timelinePreview;
  const sdk = isPreview ? ContentStackPreviewSDK : ContentStackSDK;

  // Extract variant from header (non preview/debug mode) or URL param (preview/debug mode)
  // Hybrid approach: header for clean URLs, URL param for debugging/preview
  const variantFromHeader = (req?.headers?.['x-cs-variant'] as string | undefined) ?? '';
  const variantFromUrl = (query.cs_variant as string | undefined) ?? '';
  const variantParam = variantFromHeader || variantFromUrl;
  const variantAliases: string[] = variantParam ? variantParam.split(',') : [];

  if (livePreview) {
    sdk.livePreviewQuery({
      live_preview: query.live_preview as string,
      content_type_uid: query.content_type_uid as string,
    } as LivePreviewQuery);
  }

  if (timelinePreview) {
    sdk.livePreviewQuery({
      live_preview: query.live_preview as string,
      preview_timestamp: query.preview_timestamp as string,
    } as LivePreviewQuery);
  }
  let contentStackQuery: Query = sdk.ContentType(contentTypeUid).Query() as Query;

  if (customizeQuery) {
    try {
      contentStackQuery = customizeQuery({
        query: contentStackQuery,
        sdk,
        contentTypeUid,
        language,
        queryParams: query,
      });
    } catch (err) {
      logger.error({
        message: 'customizeQuery callback failed',
        meta: { error: err, contentTypeUid },
      });
      return Promise.reject(new Error('Query customization failed', { cause: err }));
    }
  }

  contentStackQuery = contentStackQuery.language(language);

  if (limit) {
    contentStackQuery = contentStackQuery.limit(limit);
  }

  if (pagetype && !customizeQuery) {
    contentStackQuery.query({ pagetype });
  }

  if (referenceFieldPath) {
    contentStackQuery.includeReference(referenceFieldPath);

    if (referenceFieldOnly) {
      for (const referenceField in referenceFieldOnly) {
        const fields = referenceFieldOnly[referenceField];
        contentStackQuery.only(referenceField, fields);
      }
    }
  }

  // Apply variants if available
  if (variantAliases.length > 0) {
    contentStackQuery.variants(variantAliases);
  }

  let queryResult;

  try {
    queryResult = (await contentStackQuery.toJSON().find()) as [[ContentTypeSchema | undefined]];
  } catch (err: unknown) {
    if (!isContentStackError(err)) {
      logger.error({
        message: `Fetching Content for ${contentTypeUid} failure`,
        meta: {
          error: err,
        },
      });
      return Promise.reject(new Error(`Fetching Content for ${contentTypeUid} failure`));
    }
    logger.error({
      message: err.error_message,
      meta: {
        error: err,
      },
    });
    return Promise.reject(
      new Error(`Fetching Content for ${contentTypeUid} failure`, { cause: err }),
    );
  }

  const entries = queryResult[0] ?? [];
  if (multiple) {
    const filteredEntries = entries.filter(isContentEntry<ContentTypeSchema>);
    if (jsonRtePath) {
      filteredEntries.forEach((entry) => {
        // @ts-expect-error refine types
        jsonToHTML({ entry, paths: jsonRtePath });
      });
    }
    return filteredEntries;
  }
  const queryContent = entries[0];

  if (!isContentEntry<ContentTypeSchema>(queryContent)) {
    logger.error({
      message: `Content for ${contentTypeUid} does not match schema`,
      meta: {
        contentTypeUid,
        language,
        queryParams: query,
        requestUrl: req?.url ?? 'UNKNOWN',
        queryContent,
      },
    });
    return Promise.reject(new Error('Content does not match schema'));
  }

  if (jsonRtePath) {
    jsonToHTML({
      // @ts-expect-error refine types
      entry: queryContent,
      paths: jsonRtePath,
    });
  }

  return queryContent;
}
